import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import cloudApi from '../../app/api/cloudApi';

export default function ImportSyncSettings() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);

  // Event sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncStatusLoading, setSyncStatusLoading] = useState(false);
  const [syncActionLoading, setSyncActionLoading] = useState(null);

  // Import action loading state keyed by action name
  const [importLoading, setImportLoading] = useState({});
  // Last result per action
  const [importResults, setImportResults] = useState({});

  useEffect(() => {
    if (siteId && auth.user) {
      loadSiteData();
    }
  }, [siteId, auth.user]);

  useEffect(() => {
    if (site && auth.user) {
      loadSyncStatus();
    }
  }, [site, auth.user]);

  const loadSiteData = async () => {
    try {
      const response = await cloudApi.getSite(auth.user.access_token, siteId);
      setSite(response.data);
    } catch (error) {
      console.error('Error loading site:', error);
      enqueueSnackbar('Failed to load site data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadSyncStatus = useCallback(async () => {
    if (!site || !auth.user) return;
    setSyncStatusLoading(true);
    try {
      const resp = await cloudApi.getEventSyncStatus(auth.user.access_token, siteId, site.accountId);
      setSyncStatus(resp.data);
    } catch (error) {
      console.error('Error loading sync status:', error);
      setSyncStatus(null);
    } finally {
      setSyncStatusLoading(false);
    }
  }, [site, auth.user, siteId]);

  const handleSyncAction = async (action, label) => {
    setSyncActionLoading(action);
    try {
      const fn = {
        enable: cloudApi.enableEventSync,
        disable: cloudApi.disableEventSync,
        reset: cloudApi.resetImportState,
      }[action];

      await fn(auth.user.access_token, siteId, site.accountId);
      enqueueSnackbar(`${label} completed`, { variant: 'success' });
      await loadSyncStatus();
    } catch (error) {
      console.error(`Error: ${label}`, error);
      enqueueSnackbar(`${label} failed: ${error.response?.data || error.message}`, { variant: 'error' });
    } finally {
      setSyncActionLoading(null);
    }
  };

  const handleImport = async (apiMethod, actionKey, label) => {
    setImportLoading((prev) => ({ ...prev, [actionKey]: true }));
    setImportResults((prev) => ({ ...prev, [actionKey]: null }));
    try {
      const resp = await apiMethod(auth.user.access_token, siteId, site.accountId);
      const result = resp.data;
      setImportResults((prev) => ({ ...prev, [actionKey]: result }));
      enqueueSnackbar(`${label} started successfully`, { variant: 'success' });
    } catch (error) {
      console.error(`Error: ${label}`, error);
      enqueueSnackbar(`${label} failed: ${error.response?.data || error.message}`, { variant: 'error' });
    } finally {
      setImportLoading((prev) => ({ ...prev, [actionKey]: false }));
    }
  };

  const isAdmin = () => {
    try {
      if (!auth.user || !auth.user.access_token) return false;
      const payload = auth.user.access_token.split('.')[1];
      if (!payload) return false;
      const decoded = JSON.parse(atob(payload));
      return decoded?.realm_access?.roles?.includes('admin') || false;
    } catch {
      return false;
    }
  };

  if (!isAdmin()) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You do not have permission to access this page.</Typography>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!site) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Site not found</Alert>
      </Container>
    );
  }

  const importActions = [
    {
      key: 'profilesDoorController',
      label: 'Import Profiles from Door Controller',
      icon: 'mdi:account-multiple-plus',
      fn: cloudApi.importProfilesFromDoorController,
    },
    {
      key: 'profilesFaceScanner',
      label: 'Import Profiles from Face Scanner',
      icon: 'mdi:face-recognition',
      fn: cloudApi.importProfilesFromFaceScanner,
    },
    {
      key: 'accessLevelsDoorController',
      label: 'Import Access Levels from Door Controller',
      icon: 'mdi:key-plus',
      fn: cloudApi.importAccessLevelsFromDoorController,
    },
    {
      key: 'accessLevelsFaceScanner',
      label: 'Import Access Levels from Face Scanner',
      icon: 'mdi:key-star',
      fn: cloudApi.importAccessLevelsFromFaceScanner,
    },
    {
      key: 'doorsDoorController',
      label: 'Import Doors from Door Controller',
      icon: 'mdi:door',
      fn: cloudApi.importDoorsFromDoorController,
    },
    {
      key: 'devicesFaceScanner',
      label: 'Import Devices from Face Scanner',
      icon: 'mdi:devices',
      fn: cloudApi.importDevicesFromFaceScanner,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Import &amp; Sync Settings | CloudLoq</title>
      </Helmet>

      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/sites')}
            >
              Sites
            </Link>
            <Typography color="text.primary">{site.name}</Typography>
            <Typography color="text.primary">Import &amp; Sync Settings</Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Icon icon="mdi:database-import-outline" width="32" height="32" />
            <Typography variant="h4">Import &amp; Sync Settings</Typography>
            <Chip label="Admin Only" color="error" size="small" />
          </Box>

          {/* Site Info */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Site Information</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Site Name:</Typography>
              <Typography variant="body1" fontWeight="medium">{site.name}</Typography>
              <Typography variant="body2" color="text.secondary">Site ID:</Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{site.id}</Typography>
            </Box>
          </Paper>

          {/* Event Sync Section */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title="Event Sync"
              subheader="Control event synchronisation between box and cloud"
              avatar={<Icon icon="mdi:sync" width="28" height="28" />}
              action={
                <Button
                  size="small"
                  variant="text"
                  onClick={loadSyncStatus}
                  disabled={syncStatusLoading}
                  startIcon={syncStatusLoading ? <CircularProgress size={16} /> : <Icon icon="mdi:refresh" />}
                >
                  Refresh
                </Button>
              }
            />
            <CardContent>
              {syncStatus ? (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                      label={syncStatus.eventSyncEnabled ? 'Sync Enabled' : 'Sync Disabled'}
                      color={syncStatus.eventSyncEnabled ? 'success' : 'default'}
                      variant="outlined"
                    />
                    <Chip
                      label={syncStatus.importInProgress ? 'Import In Progress' : 'No Import Running'}
                      color={syncStatus.importInProgress ? 'warning' : 'default'}
                      variant="outlined"
                    />
                    {syncStatus.importCompleted && (
                      <Chip label="Import Completed" color="info" variant="outlined" />
                    )}
                  </Box>
                  {syncStatus.currentImportJobId && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Current Import Job: <code>{syncStatus.currentImportJobId}</code>
                    </Typography>
                  )}
                  {syncStatus.lastUpdatedAt && (
                    <Typography variant="body2" color="text.secondary">
                      Last Updated: {new Date(syncStatus.lastUpdatedAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {syncStatusLoading ? 'Loading status...' : 'Status unavailable'}
                </Typography>
              )}

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <LoadingButton
                  variant="contained"
                  color="success"
                  loading={syncActionLoading === 'enable'}
                  disabled={!!syncActionLoading}
                  startIcon={<Icon icon="mdi:play" />}
                  onClick={() => handleSyncAction('enable', 'Enable Event Sync')}
                >
                  Enable Sync
                </LoadingButton>
                <LoadingButton
                  variant="contained"
                  color="warning"
                  loading={syncActionLoading === 'disable'}
                  disabled={!!syncActionLoading}
                  startIcon={<Icon icon="mdi:pause" />}
                  onClick={() => handleSyncAction('disable', 'Disable Event Sync')}
                >
                  Disable Sync
                </LoadingButton>
                <LoadingButton
                  variant="outlined"
                  color="error"
                  loading={syncActionLoading === 'reset'}
                  disabled={!!syncActionLoading}
                  startIcon={<Icon icon="mdi:restart" />}
                  onClick={() => handleSyncAction('reset', 'Reset Import State')}
                >
                  Reset Import State
                </LoadingButton>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Reset Import State</strong> clears the ImportInProgress flag and restores event sync.
                  Use only when an import is stuck.
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* Import Operations Section */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title="Import Operations"
              subheader="Trigger data imports from site devices"
              avatar={<Icon icon="mdi:database-arrow-down" width="28" height="28" />}
            />
            <CardContent>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Import operations are asynchronous. Once started, they will run in the background on the box.
                  The returned Job ID can be used for tracking.
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                {importActions.map((action) => (
                  <Grid xs={12} sm={6} key={action.key}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Icon icon={action.icon} width="24" height="24" />
                        <Typography variant="subtitle2">{action.label}</Typography>
                      </Box>

                      <LoadingButton
                        variant="contained"
                        size="small"
                        fullWidth
                        loading={!!importLoading[action.key]}
                        startIcon={<Icon icon="mdi:play-circle-outline" />}
                        onClick={() => handleImport(action.fn, action.key, action.label)}
                      >
                        Start Import
                      </LoadingButton>

                      {importResults[action.key] && (
                        <Alert severity="success" sx={{ mt: 1.5 }}>
                          <Typography variant="body2">
                            {importResults[action.key].message}
                          </Typography>
                          {importResults[action.key].importJobId && (
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              Job ID: {importResults[action.key].importJobId}
                            </Typography>
                          )}
                        </Alert>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="outlined"
              startIcon={<Icon icon="mdi:arrow-left" />}
              onClick={() => navigate('/sites')}
            >
              Back to Sites
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
}
